import { useSelector } from '@xstate/react';
import { useEffect, useRef, useState } from 'react';
import { useInRouterContext, useLocation } from 'react-router';
import type { ActorRefFrom, SnapshotFrom } from 'xstate';
import { ResponseUnmaskBoundary, useResponseUnmaskActor } from './ResponseUnmaskBoundary';
import { ResponseUnmaskControls } from './ResponseUnmaskControls';
import { responseUnmaskMachine } from './responseUnmask.machine';

type Props = {
  responseId: number;
  viewerScopeKey: string;
  refreshKey?: string;
  disabled?: boolean;
  onChanged?: () => void;
};
type Snapshot = SnapshotFrom<typeof responseUnmaskMachine>;
export { selectUnmaskViewerScope } from './responseViewerScope';
const selectState = (snapshot: Snapshot) =>
  snapshot.context.attached && snapshot.context.viewValidated ? snapshot.context.state : null;
const selectError = (snapshot: Snapshot) => snapshot.context.error;
const selectRevision = (snapshot: Snapshot) => snapshot.context.revision;
const selectBusy = (snapshot: Snapshot) =>
  snapshot.matches('loading') || snapshot.matches('mutating');
const selectMayAct = (snapshot: Snapshot) => snapshot.matches('ready');

export const ResponseUnmaskPanel = (props: Props) => {
  const routed = useInRouterContext();
  return (
    <ResponseUnmaskBoundary viewerScopeKey={props.viewerScopeKey}>
      {routed ? (
        <RoutedPanel key={`${props.viewerScopeKey}:${props.responseId}`} {...props} />
      ) : (
        <ConnectedPanel key={`${props.viewerScopeKey}:${props.responseId}`} {...props} />
      )}
    </ResponseUnmaskBoundary>
  );
};
const RoutedPanel = (props: Props) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('responseId', String(props.responseId));
  return <ConnectedPanel {...props} sourcePath={`${location.pathname}?${params.toString()}`} />;
};
const ConnectedPanel = (props: Props & { sourcePath?: string }) => {
  const actor = useResponseUnmaskActor(props.responseId, props.sourcePath);
  return actor ? (
    <ScopedPanel {...props} actor={actor} />
  ) : (
    <ResponseUnmaskControls
      state={null}
      error={null}
      busy
      disabled
      onAction={() => undefined}
      onRefresh={() => undefined}
    />
  );
};

const ScopedPanel = ({
  actor,
  refreshKey,
  disabled,
  onChanged,
  sourcePath,
}: Props & { actor: ActorRefFrom<typeof responseUnmaskMachine>; sourcePath?: string }) => {
  const [attached, setAttached] = useState(false);
  useEffect(() => {
    actor.send({ type: 'ATTACH', visible: document.visibilityState === 'visible' });
    setAttached(true);
    return () => {
      actor.send({ type: 'DETACH' });
    };
  }, [actor]);
  const state = useSelector(actor, selectState);
  const error = useSelector(actor, selectError);
  const revision = useSelector(actor, selectRevision);
  const busy = useSelector(actor, selectBusy);
  const mayAct = useSelector(actor, selectMayAct);
  const onChangedRef = useRef(onChanged);
  const viewKey = sourcePath ?? 'standalone';
  const notifiedRevision = useSelector(
    actor,
    (snapshot) => snapshot.context.notifiedRevisions[viewKey] ?? 0
  );
  const priorRefreshKey = useRef(refreshKey);
  useEffect(() => {
    onChangedRef.current = onChanged;
  }, [onChanged]);
  useEffect(() => {
    if (!disabled && onChangedRef.current && revision > notifiedRevision) {
      actor.send({ type: 'VIEW_NOTIFIED', viewKey, revision });
      onChangedRef.current();
    }
  }, [actor, disabled, revision, notifiedRevision, viewKey]);
  useEffect(() => {
    if (priorRefreshKey.current !== refreshKey) {
      priorRefreshKey.current = refreshKey;
      actor.send({ type: 'REFRESH' });
    }
  }, [actor, refreshKey]);
  useEffect(() => {
    const refresh = () => {
      const visible = document.visibilityState === 'visible';
      actor.send({ type: 'VISIBILITY', visible });
      if (visible) actor.send({ type: 'REFRESH', restartPolling: false });
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [actor]);
  return (
    <ResponseUnmaskControls
      state={attached ? state : null}
      error={error}
      busy={!attached || busy}
      disabled={!attached || disabled || !mayAct}
      onAction={(action, expectedToken, termsVersion) => {
        // The machine decides: it runs the action when ready and holds a confirmed
        // one that arrives mid-refresh, so a poll cannot swallow the user's click.
        if (!disabled) actor.send({ type: 'ACT', action, expectedToken, termsVersion });
      }}
      onRefresh={() => actor.send({ type: 'REFRESH' })}
    />
  );
};
